import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Heart, X, Smiley } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Emoji from "@/components/Emoji";

export const EMOJIS = ["❤️", "🔥", "😂", "😮", "😢", "👏", "😍", "🤯", "🤣", "🥳", "✨", "🙌", "💯", "👀", "🫡", "🤔", "😎", "🥺", "😭", "🤩", "🙏", "💔", "🤝", "🎉"];

/**
 * EmojiReactionsBar — premium reaction picker.
 *  - Single tap: toggles ❤️ with a "pop" scale animation
 *  - Long-press / right-click / "more" button: opens a glassy bottom-sheet
 *    with Twemoji SVGs that look identical on every device
 */
export default function EmojiReactionsBar({ postId, myReaction, totalReactors, onChange, onCountClick, disabled }) {
  const [popOpen, setPopOpen] = useState(false);
  const [reaction, setReaction] = useState(myReaction || null);
  const [total, setTotal] = useState(totalReactors || 0);
  const [bouncing, setBouncing] = useState(false);
  const pressTimerRef = useRef(null);
  const longPressedRef = useRef(false);

  const send = async (emoji) => {
    const prev = reaction;
    const next = reaction === emoji ? null : emoji;
    setReaction(next);
    setBouncing(true);
    setTimeout(() => setBouncing(false), 320);
    setTotal((t) => {
      if (prev && next) return t;
      if (!prev && next) return t + 1;
      if (prev && !next) return t - 1;
      return t;
    });
    setPopOpen(false);
    try {
      const { data } = await api.post(`/posts/${postId}/reactions`, { emoji });
      setReaction(data.emoji);
      onChange?.(data.emoji);
    } catch {
      setReaction(prev);
      setTotal(totalReactors || 0);
      toast.error("Couldn't react");
    }
  };

  const onPressStart = () => {
    longPressedRef.current = false;
    if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
    pressTimerRef.current = setTimeout(() => {
      longPressedRef.current = true;
      setPopOpen(true);
    }, 320);
  };
  const onPressEnd = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  };

  const onTap = (e) => {
    if (longPressedRef.current) { e.preventDefault(); longPressedRef.current = false; return; }
    send(reaction || "❤️");
  };

  return (
    <div className="relative inline-flex items-center gap-1">
      <button
        type="button"
        onClick={onTap}
        onMouseDown={onPressStart}
        onMouseUp={onPressEnd}
        onMouseLeave={onPressEnd}
        onTouchStart={onPressStart}
        onTouchEnd={onPressEnd}
        onContextMenu={(e) => { e.preventDefault(); setPopOpen(true); }}
        disabled={disabled}
        data-testid={`reactions-toggle-${postId}`}
        className={`relative h-11 w-11 rounded-full flex items-center justify-center transition active:scale-90 disabled:opacity-40 ${reaction ? "bg-primary/15" : "hover:bg-secondary/60"} ${bouncing ? "animate-[reactPop_320ms_ease-out]" : ""}`}
        aria-label="React"
        style={{ touchAction: "manipulation" }}
      >
        {reaction ? (
          <Emoji char={reaction} size={28} className="drop-shadow-[0_0_8px_hsl(15_100%_55%_/_0.45)]" />
        ) : (
          <Heart size={26} weight="bold" className="text-foreground" />
        )}
      </button>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPopOpen(true); }}
        disabled={disabled}
        data-testid={`reactions-more-${postId}`}
        aria-label="More reactions"
        title="More reactions"
        className="h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-secondary/60 active:scale-90 transition disabled:opacity-40"
      >
        <Smiley size={18} weight="bold" />
      </button>
      {total > 0 && (
        onCountClick ? (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCountClick(); }}
            data-testid={`reactions-count-${postId}`}
            className="text-sm font-bold tabular-nums text-foreground/85 ml-0.5 hover:text-primary active:scale-95 transition"
            aria-label="See who reacted"
          >
            {total}
          </button>
        ) : (
          <span className="text-sm font-bold tabular-nums text-foreground/85 ml-0.5" data-testid={`reactions-count-${postId}`}>
            {total}
          </span>
        )
      )}

      <EmojiPickerSheet
        open={popOpen}
        onClose={() => setPopOpen(false)}
        onPick={send}
        activeReaction={reaction}
        postId={postId}
      />
    </div>
  );
}

/** Portaled bottom-sheet picker — uses Twemoji SVGs for crisp consistent rendering. */
function EmojiPickerSheet({ open, onClose, onPick, activeReaction, postId }) {
  useEffect(() => {
    if (!open) return;
    const close = () => onClose();
    window.addEventListener("scroll", close, { passive: true, capture: true });
    return () => window.removeEventListener("scroll", close, true);
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center sm:justify-center bg-black/55 backdrop-blur-md"
      onClick={onClose}
      data-testid={`emoji-picker-${postId}`}
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-card border-t sm:border border-white/10 sm:rounded-3xl rounded-t-3xl p-5 pb-7 shadow-2xl animate-in slide-in-from-bottom-4 duration-200"
      >
        <div className="flex items-center justify-between mb-4 px-1">
          <div>
            <h3 className="font-heading text-base font-black tracking-tight">Pick a reaction</h3>
            <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">Tap to react · long-press for more</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="emoji-picker-close"
            className="h-9 w-9 rounded-full bg-secondary/60 hover:bg-secondary flex items-center justify-center text-muted-foreground active:scale-90 transition"
          >
            <X size={16} />
          </button>
        </div>
        <div className="grid grid-cols-6 gap-2.5" data-testid="emoji-picker-grid">
          {EMOJIS.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => onPick(e)}
              data-testid={`emoji-${e}-${postId}`}
              className={`aspect-square rounded-2xl flex items-center justify-center active:scale-110 hover:scale-105 transition ${activeReaction === e ? "bg-primary/20 ring-2 ring-primary glow" : "bg-secondary/40 hover:bg-secondary"}`}
              style={{ touchAction: "manipulation" }}
            >
              <Emoji char={e} size={30} />
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

/** Inline strip showing top reactions next to the like count.
 *
 * Facebook-style: shows the reaction emoji bubbles + names of the most recent
 * reactors so the viewer can see *who* engaged, not just how many. Falls back
 * to "N reactions" when the reactor list is missing (older payloads).
 */
export function ReactionsSummary({ reactionsMap, totalReactors, myReaction, topReactors, onClick }) {
  const top = Object.entries(reactionsMap || {}).sort((a, b) => b[1] - a[1]).slice(0, 3);
  if (totalReactors === 0 && !myReaction) return null;
  const reactors = Array.isArray(topReactors) ? topReactors : [];
  const namedCount = reactors.length;
  const remaining = Math.max(0, totalReactors - namedCount);
  // "Sara, Jay and 12 others" / "Sara reacted" / "Sara and Jay reacted"
  let label = null;
  if (namedCount > 0) {
    const names = reactors.map((r) => r.is_me ? "You" : (r.name?.split(" ")[0] || "Someone"));
    if (remaining > 0) {
      label = `${names.join(", ")} and ${remaining} ${remaining === 1 ? "other" : "others"}`;
    } else if (names.length === 1) {
      label = names[0] === "You" ? "You reacted" : `${names[0]} reacted`;
    } else {
      label = `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
    }
  }
  const inner = (
    <>
      <div className="flex -space-x-1.5">
        {top.map(([e]) => (
          <span key={e} className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-card border border-border">
            <Emoji char={e} size={14} />
          </span>
        ))}
      </div>
      {label ? (
        <span className="text-sm text-foreground/85 truncate" data-testid="reactions-names">
          <span className="font-semibold">{label}</span>
        </span>
      ) : (
        <span className="font-bold text-sm tabular-nums">{totalReactors}</span>
      )}
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="flex items-center gap-2 hover:opacity-80 active:scale-[0.98] transition max-w-full"
        aria-label="See who reacted"
        data-testid="reactions-summary-tap"
      >
        {inner}
      </button>
    );
  }
  return <div className="flex items-center gap-2 max-w-full">{inner}</div>;
}
